import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, IconButton, Stack, Tooltip } from "@mui/material";
import { Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { imprimirListadoGrande } from "lib/impresionhtml";
import { imprimirComprobanteGasto } from "lib/comprobantegasto";
import { useMaterialUIController } from "context";
import { puedeAccion, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { useNavigate } from "react-router-dom";
import {
  FP,
  PageHeader,
  StatCard,
  FpDataGrid,
  ConfirmarDialog,
  useToast,
  fpInputSx,
  fpDataGridSx,
} from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import Agregargasto from "modules/gastos/agregargasto";
import CategoriasGasto from "modules/gastos/categoriasgasto";
import useAccionInicial from "lib/useAccionInicial";

function Informegastos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeBorrar = puedeAccion(roleDetalles, "gastos", "borrar");
  // Mismo criterio que el backend (gastos_categoria_guardar / _eliminar).
  const puedeEditarCategorias =
    puedeAccionPrograma(roleDetalles, "ares5510", "modificar") ||
    puedeAccionPrograma(roleDetalles, "configuracion", "modificar");
  const puedeBorrarCategorias =
    puedeAccionPrograma(roleDetalles, "ares5520", "borrar") ||
    puedeAccionPrograma(roleDetalles, "configuracion", "modificar");
  const navigate = useNavigate();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState(null);
  const sucursal = useSucursales();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [estado, setEstado] = useState("activos"); // activos | cancelados
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gastoTarget, setGastoTarget] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const toast = useToast();

  async function procesarRequerimiento(estadoOverride) {
    if (!fecha1 || !fecha2) {
      toast.aviso("Seleccione un rango de fechas");
      return;
    }
    const est = estadoOverride !== undefined ? estadoOverride : estado;
    const sucCode = nosucursal?.label ? String(nosucursal.label).split("-")[0] : "todo";
    setCargando(true);
    try {
      const { status, data } = await post("/api/consultargastos", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: sucCode || "todo",
        estado: est,
        tipodeusuario: "",
      });
      setBuscado(true);
      if (status === 200) {
        const list = (data["data"] || []).map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }));
        setRows(list);
        const monto = data?.total?.monto;
        const num = parseFloat(String(monto || "0").replace(/,/g, ""));
        setTotal(Number.isNaN(num) ? 0 : num);
      }
      if (status === 400) {
        setRows([]);
        setTotal(0);
        toast.error(data?.Error || "Sin datos para ese rango");
      }
    } finally {
      setCargando(false);
    }
  }

  function onEstadoChange(value) {
    setEstado(value);
    setRows([]);
    setTotal(0);
    if (fecha1 && fecha2) procesarRequerimiento(value);
  }

  function abrirConfirmacion(row) {
    if (tieneEcf(row)) {
      alert("No se puede eliminar un gasto que ya tiene e-CF asignado");
      return;
    }
    setGastoTarget(row);
    setConfirmOpen(true);
  }

  async function eliminarGasto() {
    if (!gastoTarget) return;
    setEliminando(true);
    try {
      const { status, data } = await post("/api/eliminargasto", { id: gastoTarget.id });
      if (status === 200) {
        const valorNum = parseFloat(String(gastoTarget.valor || "0").replace(/,/g, ""));
        setRows((prev) => prev.filter((r) => r.id !== gastoTarget.id));
        if (!Number.isNaN(valorNum)) setTotal((prev) => Math.max(0, prev - valorNum));
        setConfirmOpen(false);
        setGastoTarget(null);
        toast.exito("Gasto eliminado");
      } else {
        toast.error(data?.Error || "No se pudo eliminar el gasto");
        setConfirmOpen(false);
      }
    } catch (err) {
      toast.error("Error de red: " + err.message);
      setConfirmOpen(false);
    } finally {
      setEliminando(false);
    }
  }

  const fmtMoney = (v) =>
    Number(parseFloat(String(v || "0").replace(/,/g, ""))).toLocaleString("es-DO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const tieneEcf = (row) => {
    const ncf = String(row?.comprobantefiscal || "").trim();
    const marcado = String(row?.marcado_electronico || "").trim().toUpperCase();
    return marcado === "S" || ncf !== "";
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80, align: "center", headerAlign: "center" },
      { field: "fecha", headerName: "Fecha", width: 120, align: "center", headerAlign: "center" },
      { field: "tipogastos", headerName: "Categoría", flex: 1, minWidth: 150 },
      { field: "descripcion", headerName: "Descripción", flex: 2, minWidth: 220 },
      // Chasis del gasto (categorías con "requiere chasis"): gastos.referencia.
      { field: "referencia", headerName: "Chasis", width: 170 },
      { field: "sucursal", headerName: "Sucursal", width: 110, align: "center", headerAlign: "center" },
      {
        field: "comprobantefiscal",
        headerName: "e-CF",
        width: 150,
        align: "center",
        headerAlign: "center",
        valueGetter: (value, row) => {
          const ncf = String(value || row?.comprobantefiscal || "").trim();
          return ncf || "—";
        },
      },
      {
        field: "valor",
        headerName: "Valor",
        type: "number",
        width: 140,
        align: "right",
        headerAlign: "right",
        // Firma de DataGrid v7: recibe el valor directo, no {value}.
        valueFormatter: (value) => `$${fmtMoney(value)}`,
      },
      {
        field: "acciones",
        headerName: "Acciones",
        width: puedeBorrar ? 120 : 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        // Un gasto anulado (valor 0) no se reimprime ni se vuelve a anular:
        // en la vista de cancelados no hay acciones, igual que ares5520.
        // Anular exige ademas que el gasto no tenga e-CF asignado.
        renderCell: (params) => {
          if (estado === "cancelados") return null;
          const conEcf = tieneEcf(params.row);
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Reimprimir comprobante">
                <IconButton
                  size="small"
                  onClick={() => imprimirComprobanteGasto(params.row)}
                  sx={{ color: FP.color.navy, "&:hover": { bgcolor: FP.color.surfaceAlt } }}
                >
                  <PrintOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {puedeBorrar && (
                <Tooltip title={conEcf ? "No se puede anular: tiene e-CF asignado" : "Anular gasto"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={conEcf}
                      onClick={() => {
                        if (conEcf) return;
                        abrirConfirmacion(params.row);
                      }}
                      sx={{
                        color: conEcf ? FP.color.textMuted : FP.color.danger,
                        "&.Mui-disabled": { color: FP.color.textMuted },
                        "&:hover": conEcf ? undefined : { bgcolor: FP.color.dangerSoft },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [estado, puedeBorrar]
  );

  const isCancelados = estado === "cancelados";

  // "Agregar gasto" se abre como diálogo (antes navegaba a /gastos/agregar).
  const [agregarOpen, setAgregarOpen] = useState(false);
  const [categoriasOpen, setCategoriasOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    agregar: { abrir: () => setAgregarOpen(true) },
  });
  const alGuardarGasto = () => {
    if (fecha1 && fecha2) procesarRequerimiento();
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle={isCancelados ? "Gastos Cancelados" : "Informe de Gastos"}
        cta={
          <Stack direction="row" spacing={1.2}>
          <Button
            variant="outlined"
            startIcon={<CategoryOutlinedIcon />}
            onClick={() => setCategoriasOpen(true)}
            sx={{
              borderColor: FP.color.border,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
            }}
          >
            Categorías
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setAgregarOpen(true)}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
            }}
          >
            Agregar gasto
          </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Filtros */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
            <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
              Rango · Sucursal · Estado
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(e) => setFecha1(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(e) => setFecha2(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  onChange={(e, value) => setNosucursal(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="estado-label" shrink>
                    Estado
                  </InputLabel>
                  <Select
                    labelId="estado-label"
                    label="Estado"
                    notched
                    value={estado}
                    onChange={(e) => onEstadoChange(e.target.value)}
                  >
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="cancelados">Cancelados</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarRequerimiento()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label={isCancelados ? "Total cancelados" : "Total de gastos"}
              value={`$${fmtMoney(total)}`}
              icon={isCancelados ? CancelOutlinedIcon : TrendingDownOutlinedIcon}
              accent={isCancelados ? FP.color.danger : FP.color.warning}
            />
          </Box>
        )}

        {/* DataGrid */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: isCancelados ? FP.color.dangerSoft : FP.color.warningSoft,
                color: isCancelados ? FP.color.danger : FP.color.warning,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isCancelados ? <CancelOutlinedIcon sx={{ fontSize: 18 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              {isCancelados ? "Gastos cancelados" : "Gastos activos"}
            </Typography>
            <Button
              size="small"
              startIcon={<PrintOutlinedIcon />}
              disabled={!rows.length}
              onClick={() =>
                imprimirListadoGrande({
                  titulo: isCancelados ? "Listado de Gastos Cancelados" : "Listado de Gastos",
                  subtitulo: `Del ${fecha1} al ${fecha2}${
                    nosucursal?.label ? ` · Sucursal ${nosucursal.label}` : " · Todas las sucursales"
                  }`,
                  cabeceras: ["ID", "Fecha", "Categoría", "Descripción", "Sucursal", "Valor"],
                  filas: rows.map((r) => [
                    r.id,
                    r.fecha,
                    r.tipogastos,
                    r.descripcion,
                    r.sucursal,
                    fmtMoney(r.valor),
                  ]),
                  totales: ["", "", "", "TOTAL", "", fmtMoney(total)],
                  numericas: [5],
                })
              }
              sx={{
                ml: "auto",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 1.4,
                color: FP.color.navy,
                border: `1px solid ${FP.color.navy}`,
              }}
            >
              Imprimir PDF
            </Button>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
              {rows.length} {rows.length === 1 ? "registro" : "registros"}
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Box sx={{ height: 520 }}>
              <FpDataGrid
                rows={rows}
                columns={columns}
                loading={cargando}
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": {
                    ...fpDataGridSx["& .MuiDataGrid-cell"],
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    alignItems: "center",
                    py: 0.8,
                  },
                }}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 20, 50]}
                vacioTitulo={buscado ? "Sin gastos en ese rango" : "No hay datos para mostrar"}
                vacioSubtitulo={
                  buscado
                    ? "Pruebe con otro rango de fechas, sucursal o estado."
                    : "Seleccione los filtros y haga clic en “Procesar” para obtener resultados."
                }
              />
            </Box>
          </Box>
        </Card>

        {/* Confirmación de borrado (componente estándar de la casa) */}
        <ConfirmarDialog
          abierto={confirmOpen}
          titulo="Eliminar gasto"
          mensaje="¿Seguro que desea eliminar este gasto?"
          textoConfirmar="Eliminar"
          destructivo
          cargando={eliminando}
          onCerrar={() => setConfirmOpen(false)}
          onConfirmar={eliminarGasto}
        >
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: FP.color.surfaceAlt, borderRadius: 1.5, border: `1px solid ${FP.color.border}` }}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
              {gastoTarget?.tipogastos || "—"}
            </Typography>
            {gastoTarget?.descripcion ? (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem", mt: 0.5 }}>
                {gastoTarget.descripcion}
              </Typography>
            ) : null}
          </Box>
        </ConfirmarDialog>
      </Box>
      {/* Agregar gasto (formulario existente, a pantalla completa) */}
      <Dialog fullScreen open={categoriasOpen} onClose={() => setCategoriasOpen(false)}>
        <CategoriasGasto
          onExit={() => setCategoriasOpen(false)}
          puedeModificar={puedeEditarCategorias}
          puedeBorrar={puedeBorrarCategorias}
        />
      </Dialog>
      <Dialog fullScreen open={agregarOpen} onClose={() => setAgregarOpen(false)}>
        <Agregargasto embedded onExit={() => setAgregarOpen(false)} onSaved={alGuardarGasto} />
      </Dialog>
    </Box>
  );
}

export default Informegastos;
